import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';

// Dashboard → Yönetici sekmesi (v1.12.0): onay bekleyen işlemler ve yönetilen personelin faaliyetleri.

export interface Personel { kullaniciID: number; adSoyad: string; kullaniciRolu?: string; }

interface PersonelOzeti {
  personelID: number; adSoyad?: string;
  musteri: number; sunum: number; sozlesme: number; not: number;
  gorusmeTamamlanan: number; gorusmeBekleyen: number; gorusmeGeciken: number;
}
interface Ozet {
  personelAdlari: Record<string, string>;
  personelOzeti: PersonelOzeti[];
  musteriler: { cariID: number; adiSoyadi: string; telefon1?: string; turu?: string; kaynak?: string; eklenmeTarihi: string; aktifMi: boolean; personelID: number }[];
  sunumlar: { sunumID: number; baslik?: string; durum: string; olusturmaTarihi: string; musteri: string; personelID: number }[];
  sozlesmeler: { sozlesmeID: number; sozlesmeTakipNo: string; eklemeTarihi: string; sozlesmeFiyati: number; sozlesmeDurumu: string; onayDurumu: string; aktifMi: boolean; musteri: string; daireNo: string; projeAdi?: string; personelID: number }[];
  notlar: { notID: number; eklenmeTarihi: string; aciklama: string; takipNo?: string; cariID?: number; musteri?: string; personelID: number }[];
  gorusmeler: { id: number; leadId: number; potansiyelMusteri?: string; phone?: string; note?: string; planlananTarih: string; durum: 'Tamamlandi' | 'Bekliyor' | 'Gecikti'; personelID: number }[];
  listeLimiti: number;
}
interface BekleyenTalep { talepID: number; talepTuru: 'Onay' | 'Degisiklik' | 'Iptal'; talepTarihi: string; talepEden?: string; sozlesmeTakipNo: string; musteri: string; daireNo: string; projeAdi?: string; }

type Sekme = 'musteriler' | 'sunumlar' | 'sozlesmeler' | 'notlar' | 'gorusmeler';

const isoGun = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const gunOnce = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return isoGun(d); };
const tarih = (s?: string) => s ? new Date(s).toLocaleDateString('tr-TR') : '—';
const para = (n: number) => `${Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`;
const TALEP_TURU: Record<string, { ad: string; renk: string; zemin: string }> = {
  Onay: { ad: 'Yeni sözleşme', renk: '#b45309', zemin: '#fef3c7' },
  Degisiklik: { ad: 'Değişiklik', renk: '#1d4ed8', zemin: '#dbeafe' },
  Iptal: { ad: 'İptal', renk: '#b91c1c', zemin: '#fee2e2' },
};
const GORUSME: Record<string, { ad: string; renk: string; zemin: string }> = {
  Tamamlandi: { ad: 'Tamamlandı', renk: '#15803d', zemin: '#dcfce7' },
  Bekliyor: { ad: 'Bekliyor', renk: '#b45309', zemin: '#fef3c7' },
  Gecikti: { ad: 'Gecikti', renk: '#b91c1c', zemin: '#fee2e2' },
};

const YoneticiPaneli: React.FC<{ personel: Personel[] }> = ({ personel }) => {
  const navigate = useNavigate();
  const [personelID, setPersonelID] = useState<string>('');
  const [baslangic, setBaslangic] = useState(gunOnce(30));
  const [bitis, setBitis] = useState(isoGun(new Date()));
  const [ozet, setOzet] = useState<Ozet | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [sekme, setSekme] = useState<Sekme>('musteriler');
  const [bekleyenler, setBekleyenler] = useState<BekleyenTalep[]>([]);

  useEffect(() => {
    axiosInstance.get('/api/SozlesmeTalepleri/bekleyenler')
      .then(res => setBekleyenler(res.data.talepler || []))
      .catch(() => setBekleyenler([]));
  }, []);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const res = await axiosInstance.get('/api/Yonetici/ozet', { params: { personelID: personelID || undefined, baslangic, bitis } });
      setOzet(res.data);
    } catch {
      setOzet(null);
    } finally {
      setYukleniyor(false);
    }
  }, [personelID, baslangic, bitis]);

  useEffect(() => { yukle(); }, [yukle]);

  const ad = (id: number) => ozet?.personelAdlari[id] || personel.find(p => p.kullaniciID === id)?.adSoyad || '—';
  const kart: React.CSSProperties = { background: '#fff', borderRadius: 16, border: '1px solid #e9edf2', boxShadow: '0 1px 4px rgba(15,23,42,.06)' };
  const th: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: '#94a3b8', padding: '9px 14px', textTransform: 'uppercase', letterSpacing: '.4px', background: '#f9fafb', textAlign: 'left', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { fontSize: 13, padding: '11px 14px', borderTop: '1px solid #f4f6f8', verticalAlign: 'top' };
  const rozet = (metin: string, renk: string, zemin: string) => (
    <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 20, color: renk, background: zemin, whiteSpace: 'nowrap' }}>{metin}</span>
  );
  const inp: React.CSSProperties = { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' };

  const sekmeler: { k: Sekme; ad: string; ikon: string; n: number }[] = [
    { k: 'musteriler', ad: 'Müşteriler', ikon: 'fa-user-plus', n: ozet?.personelOzeti.reduce((t, p) => t + p.musteri, 0) ?? 0 },
    { k: 'sunumlar', ad: 'Sunumlar', ikon: 'fa-chalkboard', n: ozet?.personelOzeti.reduce((t, p) => t + p.sunum, 0) ?? 0 },
    { k: 'sozlesmeler', ad: 'Sözleşmeler', ikon: 'fa-file-contract', n: ozet?.personelOzeti.reduce((t, p) => t + p.sozlesme, 0) ?? 0 },
    { k: 'notlar', ad: 'Notlar', ikon: 'fa-sticky-note', n: ozet?.personelOzeti.reduce((t, p) => t + p.not, 0) ?? 0 },
    { k: 'gorusmeler', ad: 'P. Müşteri Görüşmeleri', ikon: 'fa-phone', n: ozet?.personelOzeti.reduce((t, p) => t + p.gorusmeTamamlanan + p.gorusmeBekleyen + p.gorusmeGeciken, 0) ?? 0 },
  ];

  const bos = (metin: string) => <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{metin}</div>;

  const liste = () => {
    if (!ozet) return bos('Veriler yüklenemedi.');
    switch (sekme) {
      case 'musteriler':
        return ozet.musteriler.length === 0 ? bos('Bu dönemde eklenen müşteri yok.') : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Müşteri</th><th style={th}>Telefon</th><th style={th}>Kaynak</th><th style={th}>Ekleyen</th><th style={th}>Tarih</th></tr></thead>
            <tbody>{ozet.musteriler.map(m => (
              <tr key={m.cariID} style={{ cursor: 'pointer' }} onClick={() => navigate(m.turu === 'Müşteri' ? `/musteriler/${m.cariID}` : `/cariler/${m.cariID}`)}>
                <td style={{ ...td, fontWeight: 600 }}>{m.adiSoyadi}{!m.aktifMi && <span style={{ marginLeft: 6 }}>{rozet('Pasif', '#64748b', '#f1f5f9')}</span>}</td>
                <td style={td}>{m.telefon1 || '—'}</td><td style={td}>{m.kaynak || '—'}</td>
                <td style={td}>{ad(m.personelID)}</td><td style={td}>{tarih(m.eklenmeTarihi)}</td>
              </tr>))}</tbody>
          </table>);
      case 'sunumlar':
        return ozet.sunumlar.length === 0 ? bos('Bu dönemde hazırlanan sunum yok.') : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Sunum</th><th style={th}>Müşteri</th><th style={th}>Durum</th><th style={th}>Hazırlayan</th><th style={th}>Tarih</th></tr></thead>
            <tbody>{ozet.sunumlar.map(s => (
              <tr key={s.sunumID} style={{ cursor: 'pointer' }} onClick={() => navigate(`/musteri-sunumlari/${s.sunumID}`)}>
                <td style={{ ...td, fontWeight: 600 }}>{s.baslik || `Sunum #${s.sunumID}`}</td><td style={td}>{s.musteri}</td>
                <td style={td}>{s.durum}</td><td style={td}>{ad(s.personelID)}</td><td style={td}>{tarih(s.olusturmaTarihi)}</td>
              </tr>))}</tbody>
          </table>);
      case 'sozlesmeler':
        return ozet.sozlesmeler.length === 0 ? bos('Bu dönemde yapılan sözleşme yok.') : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Sözleşme</th><th style={th}>Müşteri / Daire</th><th style={th}>Tutar</th><th style={th}>Durum</th><th style={th}>Yapan</th><th style={th}>Tarih</th></tr></thead>
            <tbody>{ozet.sozlesmeler.map(s => (
              <tr key={s.sozlesmeID} style={{ cursor: 'pointer' }} onClick={() => navigate(`/sozlesme-detay/${s.sozlesmeTakipNo}`)}>
                <td style={{ ...td, fontWeight: 600 }}>{s.sozlesmeTakipNo}</td>
                <td style={td}>{s.musteri}<div style={{ fontSize: 12, color: '#64748b' }}>{[s.projeAdi, s.daireNo].filter(Boolean).join(' · ')}</div></td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{para(s.sozlesmeFiyati)}</td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {rozet(s.sozlesmeDurumu === 'ArsaSahibi' ? 'Arsa Sahibi' : s.sozlesmeDurumu, '#334155', '#f1f5f9')}
                    {!s.aktifMi ? rozet('İptal', '#b91c1c', '#fee2e2')
                      : s.onayDurumu === 'Bekliyor' ? rozet('Onay Bekliyor', '#b45309', '#fef3c7')
                      : s.onayDurumu === 'Reddedildi' ? rozet('Reddedildi', '#b91c1c', '#fee2e2') : null}
                  </div>
                </td>
                <td style={td}>{ad(s.personelID)}</td><td style={td}>{tarih(s.eklemeTarihi)}</td>
              </tr>))}</tbody>
          </table>);
      case 'notlar':
        return ozet.notlar.length === 0 ? bos('Bu dönemde eklenen not yok.') : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Not</th><th style={th}>İlgili</th><th style={th}>Ekleyen</th><th style={th}>Tarih</th></tr></thead>
            <tbody>{ozet.notlar.map(n => (
              <tr key={n.notID} style={{ cursor: n.cariID ? 'pointer' : 'default' }} onClick={() => n.cariID && navigate(`/musteriler/${n.cariID}`)}>
                <td style={{ ...td, maxWidth: 420, whiteSpace: 'pre-line' }}>{n.aciklama}</td>
                <td style={td}>{n.musteri || n.takipNo || '—'}</td><td style={td}>{ad(n.personelID)}</td><td style={td}>{tarih(n.eklenmeTarihi)}</td>
              </tr>))}</tbody>
          </table>);
      case 'gorusmeler':
        return ozet.gorusmeler.length === 0 ? bos('Bu dönemde planlanmış potansiyel müşteri görüşmesi yok.') : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Potansiyel müşteri</th><th style={th}>Planlanan</th><th style={th}>Durum</th><th style={th}>Not</th><th style={th}>Personel</th></tr></thead>
            <tbody>{ozet.gorusmeler.map(g => (
              <tr key={g.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/lead-takip')}>
                <td style={{ ...td, fontWeight: 600 }}>{g.potansiyelMusteri || '—'}<div style={{ fontSize: 12, color: '#64748b', fontWeight: 400 }}>{g.phone}</div></td>
                <td style={td}>{tarih(g.planlananTarih)}</td>
                <td style={td}>{rozet(GORUSME[g.durum].ad, GORUSME[g.durum].renk, GORUSME[g.durum].zemin)}</td>
                <td style={{ ...td, maxWidth: 320, color: '#475569' }}>{g.note || '—'}</td>
                <td style={td}>{ad(g.personelID)}</td>
              </tr>))}</tbody>
          </table>);
    }
  };

  const seciliSayi = sekmeler.find(s => s.k === sekme)?.n ?? 0;
  const listeKesildi = ozet && seciliSayi > ozet.listeLimiti;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Onay bekleyenler */}
      <div style={kart}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a' }}>
            <i className="fas fa-user-check" style={{ color: '#cd6906', marginRight: 8 }}></i>Onay Bekleyen İşlemler
            <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: bekleyenler.length ? '#fef3c7' : '#f1f5f9', color: bekleyenler.length ? '#b45309' : '#64748b' }}>{bekleyenler.length}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="qa-btn qa-outline" onClick={() => navigate('/sozlesme-onaylari?sekme=gecmis')}><i className="fas fa-history"></i>Geçmiş onaylar</button>
            {bekleyenler.length > 0 && <button type="button" className="qa-btn qa-primary" onClick={() => navigate('/sozlesme-onaylari')}>Tümünü gör</button>}
          </div>
        </div>
        {bekleyenler.length === 0 ? bos('Karar bekleyen işlem yok.') : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>{bekleyenler.slice(0, 5).map(b => (
                <tr key={b.talepID} style={{ cursor: 'pointer' }} onClick={() => navigate(`/sozlesme-detay/${b.sozlesmeTakipNo}`)}>
                  <td style={td}>{rozet(TALEP_TURU[b.talepTuru].ad, TALEP_TURU[b.talepTuru].renk, TALEP_TURU[b.talepTuru].zemin)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{b.musteri}<div style={{ fontSize: 12, color: '#64748b', fontWeight: 400 }}>{[b.projeAdi, b.daireNo].filter(Boolean).join(' · ')}</div></td>
                  <td style={td}>{b.talepEden || '—'}</td>
                  <td style={{ ...td, color: '#64748b', whiteSpace: 'nowrap' }}>{new Date(b.talepTarihi).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                </tr>))}</tbody>
            </table>
          </div>
        )}
      </div>

      {/* Filtre */}
      <div style={{ ...kart, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a', marginRight: 'auto' }}>
          <i className="fas fa-users" style={{ color: '#cd6906', marginRight: 8 }}></i>Personel Faaliyetleri
        </div>
        <select value={personelID} onChange={e => setPersonelID(e.target.value)} style={{ ...inp, minWidth: 180 }}>
          <option value="">Tüm personel ({personel.length})</option>
          {personel.map(p => <option key={p.kullaniciID} value={p.kullaniciID}>{p.adSoyad}</option>)}
        </select>
        <input type="date" value={baslangic} max={bitis} onChange={e => e.target.value && setBaslangic(e.target.value)} style={inp} />
        <span style={{ color: '#94a3b8' }}>–</span>
        <input type="date" value={bitis} min={baslangic} onChange={e => e.target.value && setBitis(e.target.value)} style={inp} />
        {[[7, '7 gün'], [30, '30 gün'], [90, '90 gün']].map(([n, etiket]) => (
          <button key={n} type="button" className="qa-btn qa-outline" style={{ padding: '6px 11px', fontSize: 12 }}
            onClick={() => { setBaslangic(gunOnce(n as number)); setBitis(isoGun(new Date())); }}>{etiket}</button>
        ))}
      </div>

      {/* Personel özeti */}
      <div style={kart}>
        {yukleniyor && !ozet ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...</div>
        ) : !ozet || ozet.personelOzeti.length === 0 ? bos('Yönettiğiniz personel bulunmuyor.') : (
          <div style={{ overflowX: 'auto', opacity: yukleniyor ? 0.6 : 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead><tr>
                <th style={th}>Personel</th><th style={th}>Müşteri</th><th style={th}>Sunum</th><th style={th}>Sözleşme</th><th style={th}>Not</th>
                <th style={th}>Görüşme ✓</th><th style={th}>Bekleyen</th><th style={th}>Geciken</th>
              </tr></thead>
              <tbody>{ozet.personelOzeti.map(p => (
                <tr key={p.personelID} style={{ cursor: 'pointer', background: String(p.personelID) === personelID ? '#fff7ed' : undefined }}
                  onClick={() => setPersonelID(String(p.personelID) === personelID ? '' : String(p.personelID))} title="Bu personele göre filtrele">
                  <td style={{ ...td, fontWeight: 600 }}>{p.adSoyad}</td>
                  <td style={td}>{p.musteri}</td><td style={td}>{p.sunum}</td><td style={td}>{p.sozlesme}</td><td style={td}>{p.not}</td>
                  <td style={{ ...td, color: '#15803d', fontWeight: 600 }}>{p.gorusmeTamamlanan}</td>
                  <td style={{ ...td, color: '#b45309', fontWeight: 600 }}>{p.gorusmeBekleyen}</td>
                  <td style={{ ...td, color: p.gorusmeGeciken ? '#b91c1c' : '#94a3b8', fontWeight: 600 }}>{p.gorusmeGeciken}</td>
                </tr>))}</tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ayrıntı listeleri */}
      <div style={kart}>
        <div style={{ display: 'flex', gap: 4, padding: '10px 12px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
          {sekmeler.map(s => (
            <button key={s.k} type="button" onClick={() => setSekme(s.k)}
              style={{ padding: '7px 12px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: sekme === s.k ? '#fff7ed' : 'transparent', color: sekme === s.k ? '#cd6906' : '#64748b' }}>
              <i className={`fas ${s.ikon}`} style={{ marginRight: 6 }}></i>{s.ad} <span style={{ opacity: 0.7 }}>({s.n})</span>
            </button>
          ))}
        </div>
        <div style={{ overflowX: 'auto', opacity: yukleniyor ? 0.6 : 1 }}>{liste()}</div>
        {listeKesildi && (
          <div style={{ padding: '8px 16px', fontSize: 12, color: '#64748b', borderTop: '1px solid #f1f5f9' }}>
            İlk {ozet!.listeLimiti} kayıt gösteriliyor; daha dar bir tarih aralığı ya da personel seçin.
          </div>
        )}
      </div>
    </div>
  );
};

export default YoneticiPaneli;
